import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, closestCenter, TouchSensor, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FULL_STOP_MAP from '../data/FULL_STOP_MAP.js';
import './dnd.css';

// Funkcja pomocnicza do tworzenia pełnej nazwy przystanku (taka sama jak w hookach)
const getFullStopName = (stop) => {
  const stopIdString = stop.stopId.toString();

  // 1. Sprawdzamy mapowanie dla stopId w statycznym pliku
  const mappedName = FULL_STOP_MAP[stopIdString];

  if (mappedName) {
    return mappedName; // Np. "Witomino Centrum 01"
  }

  // 2. Logika awaryjna (jeśli API Tristar ma subName)
  if (stop.subName) {
    return `${stop.stopDesc} ${stop.subName}`;
  }

  // 3. Ostatecznie używamy głównej nazwy
  return stop.stopDesc;
};

// Komponent sortowalnego elementu listy
function SortableItem({ stop, userLocation, sortOrder, calculateDistance, toggleFavorite, fullStopName }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: stop.stopId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    touchAction: 'none' // Ważne dla touch listeners
  };

  return (
    <div ref={setNodeRef} style={style} className="stop-item-wrapper">
      {/* Uchwyt do przeciągania */}
      <div className="drag-handle" {...attributes} {...listeners} aria-label="Przeciągnij aby zmienić kolejność">
        <span className="drag-icon" aria-hidden="true">☰</span>
      </div>

      <Link
        to={`/stop/${stop.stopId}`}
        className="stop-item favorite-stop"
        style={{ flex: 1, textDecoration: 'none' }}
      >
        <div className="stop-info">
          <div className="stop-name">{fullStopName}</div>
          <div className="stop-code">Kod: {stop.stopId}</div>
          {userLocation && sortOrder === 'distance' && (
            <div className="stop-distance">
              {calculateDistance(
                userLocation.latitude,
                userLocation.longitude,
                stop.stopLat,
                stop.stopLon
              ).toFixed(1)} km od Ciebie
            </div>
          )}
        </div>
      </Link>

      {/* Serduszko poza Linkiem, żeby kliknięcie nie przenosiło */}
      <button
        className="favorite-icon"
        onClick={(e) => toggleFavorite(e, stop.stopId)}
        aria-label="Usuń z ulubionych"
      >
        <span className="heart-filled" aria-hidden="true">♥</span>
      </button>
    </div>
  );
}

function FavoriteStopsList({ favoriteStopsData, userLocation, sortOrder, toggleFavorite, calculateDistance, onReorder }) {
  const [isEditing, setIsEditing] = useState(false);

  // Sensory dla myszki i dotyku
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        tolerance: 5,
        delay: 100,
      },
    })
  );

  if (!favoriteStopsData || favoriteStopsData.length === 0) return null;

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = favoriteStopsData.findIndex((stop) => stop.stopId === active.id);
      const newIndex = favoriteStopsData.findIndex((stop) => stop.stopId === over.id);

      const newOrder = arrayMove(favoriteStopsData, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  return (
    <section className="favorites-section" aria-labelledby="favorites-heading">
      <div className="favorites-header">
        <h2 id="favorites-heading">Ulubione przystanki</h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="edit-favorites-btn"
          aria-pressed={isEditing}
        >
          {isEditing ? 'Gotowe' : 'Edytuj'}
        </button>
      </div>

      <div className="favorites-list" role="list">
        {isEditing ? (
          // TRYB EDYCJI - DnD
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={favoriteStopsData.map(s => s.stopId)}
              strategy={verticalListSortingStrategy}
            >
              {favoriteStopsData.map(stop => {
                const fullStopName = getFullStopName(stop);
                let displayName = fullStopName;
                if (!displayName) {
                  displayName = stop.fullStopName || stop.stopName || "Brak nazwy";
                }

                return (
                  <SortableItem
                    key={stop.stopId}
                    stop={stop}
                    userLocation={userLocation}
                    sortOrder={sortOrder}
                    calculateDistance={calculateDistance}
                    toggleFavorite={toggleFavorite}
                    fullStopName={displayName}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        ) : (
          // TRYB PRZEGLĄDANIA - Zwykła lista (Link)
          favoriteStopsData.map(stop => {
            const fullStopName = getFullStopName(stop);
            let displayName = fullStopName;
            if (!displayName) {
              displayName = stop.fullStopName || stop.stopName || "Brak nazwy";
            }

            return (
              <Link
                to={`/stop/${stop.stopId}`}
                key={stop.stopId}
                className="stop-item favorite-stop"
                role="listitem"
              >
                <div className="stop-info">
                  <div className="stop-name">{displayName}</div>
                  <div className="stop-code">Kod: {stop.stopId}</div>
                  {userLocation && sortOrder === 'distance' && (
                    <div className="stop-distance">
                      {calculateDistance(
                        userLocation.latitude,
                        userLocation.longitude,
                        stop.stopLat,
                        stop.stopLon
                      ).toFixed(1)} km od Ciebie
                    </div>
                  )}
                </div>
                <button
                  className="favorite-icon"
                  onClick={(e) => toggleFavorite(e, stop.stopId)}
                  aria-label="Usuń z ulubionych"
                >
                  <span className="heart-filled" aria-hidden="true">♥</span>
                </button>
              </Link>
            );
          })
        )}
      </div>
    </section>
  );
}

export default FavoriteStopsList;